<template>
  <div class="modal-overlay" @click="$emit('close')">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h2 class="modal-title">
          <i class="fas fa-exchange-alt"></i>
          Nouveau Mouvement de Stock
        </h2>
        <button @click="$emit('close')" class="close-btn">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="modal-body">
        <!-- Indicateur de progression -->
        <div class="progress-indicator">
          <div class="progress-steps">
            <div 
              v-for="(step, index) in steps" 
              :key="index"
              class="progress-step"
              :class="{ 
                'active': currentStep === index + 1, 
                'completed': currentStep > index + 1 
              }"
              @click="goToStep(index + 1)"
            >
              <div class="step-number">
                <i v-if="currentStep > index + 1" class="fas fa-check"></i>
                <span v-else>{{ index + 1 }}</span>
              </div>
              <div class="step-label">{{ step.title }}</div>
            </div>
          </div>
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
          </div>
        </div>

        <form @submit.prevent="handleSubmit" class="wizard-form">
          <!-- Étape 1: Type de mouvement -->
          <div v-show="currentStep === 1" class="step-content">
            <div class="step-header">
              <h3 class="step-title">
                <i class="fas fa-exchange-alt"></i>
                Sélectionner le Type de Mouvement
              </h3>
              <p class="step-description">Choisissez le type de mouvement de stock que vous souhaitez créer</p>
            </div>
            <div class="movement-types-compact">
              <label v-for="type in movementTypes" :key="type.value" class="movement-type-card-compact">
                <input
                  type="radio"
                  v-model="form.typeMouvement"
                  :value="type.value"
                  @change="onTypeChange"
                />
                <div class="card-content-compact">
                  <i :class="type.icon"></i>
                  <span>{{ type.label }}</span>
                </div>
              </label>
            </div>
          </div>

          <!-- Étape 2: Produit et Quantité -->
          <div v-show="currentStep === 2" class="step-content">
            <div class="step-header">
              <h3 class="step-title">
                <i class="fas fa-box"></i>
                Produit et Quantité
              </h3>
              <p class="step-description">Sélectionnez le produit concerné et définissez la quantité</p>
            </div>
            <div class="form-grid">
              <div class="form-group">
                <label for="produit" class="required">Produit</label>
                <select
                  id="produit"
                  v-model="form.produit"
                  :class="{ error: errors.produit }"
                  required
                  @change="onProductChange"
                >
                  <option value="">Sélectionner un produit</option>
                  <option v-for="product in availableProducts" :key="product._id" :value="product._id">
                    {{ product.referenceInterne }} - {{ product.designation }}
                  </option>
                </select>
                <span v-if="errors.produit" class="error-message">{{ errors.produit }}</span>
              </div>
              
              <div class="form-group">
                <label for="quantite" class="required">Quantité</label>
                <input
                  type="number"
                  id="quantite"
                  v-model.number="form.quantite"
                  :class="{ error: errors.quantite }"
                  required
                  :min="isOutgoingMovement ? -999 : 1"
                  :max="isOutgoingMovement ? -1 : 999"
                />
                <span v-if="errors.quantite" class="error-message">{{ errors.quantite }}</span>
                <small class="help-text">
                  {{ isOutgoingMovement ? 'Quantité négative pour sortie' : 'Quantité positive pour entrée' }}
                </small>
              </div>
            </div>
          </div>

          <!-- Étape 3: Emplacements -->
          <div v-show="currentStep === 3" class="step-content">
            <div class="step-header">
              <h3 class="step-title">
                <i class="fas fa-map-marker-alt"></i>
                Emplacements
              </h3>
              <p class="step-description">Définissez les emplacements d'origine et de destination</p>
            </div>
            <div class="form-grid">
              <div class="form-group">
                <label for="emplacementOrigine" class="required">De</label>
                <select
                  id="emplacementOrigine"
                  v-model="form.emplacementOrigine"
                  :class="{ error: errors.emplacementOrigine }"
                  required
                >
                  <option value="">Sélectionner origine</option>
                  <option v-for="location in locations" :key="location.value" :value="location.value">
                    {{ location.label }}
                  </option>
                </select>
                <span v-if="errors.emplacementOrigine" class="error-message">{{ errors.emplacementOrigine }}</span>
              </div>
              
              <div class="form-group">
                <label for="emplacementDestination" class="required">Vers</label>
                <select
                  id="emplacementDestination"
                  v-model="form.emplacementDestination"
                  :class="{ error: errors.emplacementDestination }"
                  required
                >
                  <option value="">Sélectionner destination</option>
                  <option v-for="location in locations" :key="location.value" :value="location.value">
                    {{ location.label }}
                  </option>
                </select>
                <span v-if="errors.emplacementDestination" class="error-message">{{ errors.emplacementDestination }}</span>
              </div>
            </div>
          </div>

          <!-- Étape 4: Détails -->
          <div v-show="currentStep === 4" class="step-content">
            <div class="step-header">
              <h3 class="step-title">
                <i class="fas fa-info-circle"></i>
                Informations Complémentaires
              </h3>
              <p class="step-description">Ajoutez les détails et justifications nécessaires</p>
            </div>
            <div class="form-grid">
              <div class="form-group" v-if="needsTechnician">
                <label for="technicienConcerne">Technicien</label>
                <select id="technicienConcerne" v-model="form.technicienConcerne">
                  <option value="">Sélectionner un technicien</option>
                  <option v-for="tech in technicians" :key="tech._id" :value="tech._id">
                    {{ tech.prenom }} {{ tech.nom }}
                  </option>
                </select>
              </div>
              
              <div class="form-group" v-if="needsClient">
                <label for="clientConcerne">Client</label>
                <input
                  type="text"
                  id="clientConcerne"
                  v-model="form.clientConcerne"
                  placeholder="Nom du client"
                />
              </div>
              
              <div class="form-group">
                <label for="motif" class="required">Motif</label>
                <textarea
                  id="motif"
                  v-model="form.motif"
                  :class="{ error: errors.motif }"
                  placeholder="Raison du mouvement"
                  rows="3"
                  required
                ></textarea>
                <span v-if="errors.motif" class="error-message">{{ errors.motif }}</span>
              </div>
              
              <div class="form-group" v-if="needsJustification">
                <label for="justification" class="required">Justification</label>
                <textarea
                  id="justification"
                  v-model="form.justification"
                  :class="{ error: errors.justification }"
                  placeholder="Justification obligatoire pour ajustement"
                  rows="3"
                  required
                ></textarea>
                <span v-if="errors.justification" class="error-message">{{ errors.justification }}</span>
              </div>
            </div>
          </div>
        </form>
      </div>
      
      <div class="modal-footer">
        <div class="footer-left">
          <button type="button" @click="$emit('close')" class="btn btn-secondary">
            <i class="fas fa-times"></i>
            Annuler
          </button>
        </div>
        
        <div class="footer-right">
          <button 
            v-if="currentStep > 1" 
            type="button" 
            @click="previousStep" 
            class="btn btn-outline"
          >
            <i class="fas fa-chevron-left"></i>
            Retour
          </button>
          
          <button 
            v-if="currentStep < totalSteps" 
            type="button" 
            @click="nextStep" 
            class="btn btn-primary"
            :disabled="!canProceedToNextStep"
          >
            Suivant
            <i class="fas fa-chevron-right"></i>
          </button>
          
          <button 
            v-if="currentStep === totalSteps" 
            type="submit" 
            @click="handleSubmit" 
            class="btn btn-primary" 
            :disabled="loading || !isFormValid"
          >
            <i v-if="loading" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-save"></i>
            Créer le Mouvement
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed } from 'vue'

export default {
  name: 'MovementModal',
  props: {
    movement: {
      type: Object,
      default: null
    }
  },
  emits: ['close', 'save'],
  setup(props, { emit }) {
    const loading = ref(false)
    const errors = reactive({})
    const currentStep = ref(1)
    const totalSteps = 4
    
    const steps = [
      { title: 'Type', icon: 'fas fa-exchange-alt' },
      { title: 'Produit', icon: 'fas fa-box' },
      { title: 'Emplacements', icon: 'fas fa-map-marker-alt' },
      { title: 'Détails', icon: 'fas fa-info-circle' }
    ]
    
    const form = reactive({
      typeMouvement: '',
      produit: '',
      quantite: 1,
      emplacementOrigine: '',
      emplacementDestination: '',
      technicienConcerne: '',
      clientConcerne: '',
      motif: '',
      justification: ''
    })

    // Types de mouvements
    const movementTypes = [
      { value: 'entree_achat', label: 'Entrée Achat', icon: 'fas fa-truck' },
      { value: 'sortie_technicien', label: 'Sortie Technicien', icon: 'fas fa-user-cog' },
      { value: 'consommation_client', label: 'Consommation Client', icon: 'fas fa-user' },
      { value: 'retour_non_consomme', label: 'Retour Non Consommé', icon: 'fas fa-undo' },
      { value: 'retour_defectueux', label: 'Retour Défectueux', icon: 'fas fa-exclamation-triangle' },
      { value: 'inventaire_ajustement', label: 'Ajustement Inventaire', icon: 'fas fa-balance-scale' }
    ]

    // Emplacements
    const locations = [
      { value: 'magasin_central', label: 'Magasin Central' },
      { value: 'sac_technicien', label: 'Technicien' },
      { value: 'chez_client', label: 'Chez Client' },
      { value: 'sav_defectueux', label: 'SAV/Défectueux' },
      { value: 'fournisseur', label: 'Fournisseur' }
    ]

    // Mock data - à remplacer par des appels API
    const availableProducts = ref([])
    const technicians = ref([])

    // Computed
    const isOutgoingMovement = computed(() => {
      return ['sortie_technicien', 'consommation_client', 'retour_defectueux'].includes(form.typeMouvement)
    })

    const needsTechnician = computed(() => {
      return ['sortie_technicien', 'consommation_client', 'retour_non_consomme'].includes(form.typeMouvement)
    })

    const needsClient = computed(() => {
      return form.typeMouvement === 'consommation_client'
    })

    const needsJustification = computed(() => {
      return form.typeMouvement === 'inventaire_ajustement'
    })

    // Méthodes
    const onTypeChange = () => {
      // Ajuster la quantité selon le type
      if (isOutgoingMovement.value && form.quantite > 0) {
        form.quantite = -Math.abs(form.quantite)
      } else if (!isOutgoingMovement.value && form.quantite < 0) {
        form.quantite = Math.abs(form.quantite)
      }
    }

    const onProductChange = () => {
      // Logique lors du changement de produit
    }

    const validateForm = () => {
      Object.keys(errors).forEach(key => delete errors[key])
      
      if (!form.typeMouvement) {
        errors.typeMouvement = 'Type de mouvement requis'
      }
      
      if (!form.produit) {
        errors.produit = 'Produit requis'
      }
      
      if (!form.quantite || form.quantite === 0) {
        errors.quantite = 'Quantité requise et différente de 0'
      }
      
      if (!form.emplacementOrigine) {
        errors.emplacementOrigine = 'Emplacement origine requis'
      }
      
      if (!form.emplacementDestination) {
        errors.emplacementDestination = 'Emplacement destination requis'
      }
      
      if (!form.motif.trim()) {
        errors.motif = 'Motif requis'
      }
      
      if (needsJustification.value && !form.justification.trim()) {
        errors.justification = 'Justification requise pour ajustement inventaire'
      }
      
      return Object.keys(errors).length === 0
    }

    const handleSubmit = async () => {
      if (!validateForm()) {
        return
      }

      loading.value = true
      try {
        const movementData = { ...form }
        emit('save', movementData)
      } catch (error) {
        console.error('Erreur lors de la sauvegarde:', error)
      } finally {
        loading.value = false
      }
    }

    // Computed properties pour la navigation par étapes
    const progressPercentage = computed(() => {
      return (currentStep.value / totalSteps) * 100
    })

    const canProceedToNextStep = computed(() => {
      switch (currentStep.value) {
        case 1:
          return form.typeMouvement !== ''
        case 2:
          return form.produit !== '' && form.quantite !== 0
        case 3:
          return form.emplacementOrigine !== '' && form.emplacementDestination !== ''
        case 4:
          return form.motif.trim() !== '' && (!needsJustification.value || form.justification.trim() !== '')
        default:
          return false
      }
    })

    const isFormValid = computed(() => {
      return form.typeMouvement !== '' &&
             form.produit !== '' &&
             form.quantite !== 0 &&
             form.emplacementOrigine !== '' &&
             form.emplacementDestination !== '' &&
             form.motif.trim() !== '' &&
             (!needsJustification.value || form.justification.trim() !== '')
    })

    // Méthodes de navigation
    const nextStep = () => {
      if (currentStep.value < totalSteps && canProceedToNextStep.value) {
        currentStep.value++
      }
    }

    const previousStep = () => {
      if (currentStep.value > 1) {
        currentStep.value--
      }
    }

    const goToStep = (step) => {
      if (step <= currentStep.value || canProceedToNextStep.value) {
        currentStep.value = step
      }
    }

    return {
      form,
      errors,
      loading,
      currentStep,
      totalSteps,
      steps,
      progressPercentage,
      canProceedToNextStep,
      isFormValid,
      movementTypes,
      availableProducts,
      locations,
      technicians,
      isOutgoingMovement,
      needsTechnician,
      needsClient,
      needsJustification,
      onTypeChange,
      onProductChange,
      handleSubmit,
      nextStep,
      previousStep,
      goToStep
    }
  }
}
</script>

<style scoped>
/* Animations professionnelles */
@keyframes modalSlideIn {
  0% {
    opacity: 0;
    transform: translateY(-30px) scale(0.95);
    backdrop-filter: blur(0px);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
    backdrop-filter: blur(10px);
  }
}

@keyframes fadeInUp {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}

@keyframes shimmer {
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 27, 183, 0.2) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  backdrop-filter: blur(8px);
  animation: modalSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-content {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.99) 100%);
  border-radius: 24px;
  box-shadow: 
    0 25px 50px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(255, 255, 255, 0.8),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  max-width: 900px;
  width: 100%;
  max-height: 95vh;
  overflow: hidden;
  position: relative;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(226, 232, 240, 0.6);
  animation: fadeInUp 0.5s ease-out;
}

.modal-content::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 50%, #004085 100%);
  border-radius: 24px 24px 0 0;
  box-shadow: 0 2px 10px rgba(0, 123, 255, 0.4);
}

.modal-content::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 20%, rgba(0, 123, 255, 0.03) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(0, 86, 179, 0.02) 0%, transparent 50%);
  pointer-events: none;
  border-radius: 24px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem 2.5rem 1.5rem;
  border-bottom: 1px solid rgba(226, 232, 240, 0.5);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.95) 100%);
  border-radius: 24px 24px 0 0;
  position: relative;
}

.modal-header::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 50%;
  transform: translateX(-50%);
  width: 100px;
  height: 2px;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  border-radius: 1px;
}

.modal-title {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 800;
  background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  gap: 1rem;
  letter-spacing: -0.02em;
}

.modal-title i {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 50%, #004085 100%);
  color: white;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  box-shadow: 0 8px 25px rgba(0, 123, 255, 0.3);
  animation: pulse 2s infinite;
}

.close-btn {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%);
  border: 1px solid rgba(239, 68, 68, 0.2);
  font-size: 1.25rem;
  cursor: pointer;
  color: #ef4444;
  padding: 0;
  border-radius: 16px;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
}

.close-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  transition: left 0.5s ease;
}

.close-btn:hover::before {
  left: 100%;
}

.close-btn:hover {
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.15) 100%);
  border-color: rgba(239, 68, 68, 0.4);
  transform: scale(1.05) rotate(90deg);
  box-shadow: 0 8px 25px rgba(239, 68, 68, 0.3);
}

.modal-body {
  padding: 2rem 2.5rem;
  max-height: calc(95vh - 200px);
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 123, 255, 0.3) transparent;
}

.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: transparent;
}

.modal-body::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  border-radius: 3px;
}

/* Indicateur de progression */
.progress-indicator {
  margin-bottom: 2rem;
  padding: 0 1rem;
}

.progress-steps {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  position: relative;
}

.progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 2;
}

.progress-step:hover {
  transform: translateY(-2px);
}

.step-number {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(135deg, rgba(107, 114, 128, 0.1) 0%, rgba(156, 163, 175, 0.05) 100%);
  border: 2px solid rgba(107, 114, 128, 0.3);
  color: #6b7280;
}

.progress-step.active .step-number {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 50%, #004085 100%);
  border-color: #007bff;
  color: white;
  box-shadow: 0 8px 25px rgba(0, 123, 255, 0.3);
  animation: pulse 2s infinite;
}

.progress-step.completed .step-number {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-color: #10b981;
  color: white;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
}

.step-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #6b7280;
  text-align: center;
  transition: all 0.3s ease;
}

.progress-step.active .step-label {
  color: #007bff;
  font-weight: 700;
}

.progress-step.completed .step-label {
  color: #10b981;
  font-weight: 700;
}

.progress-bar {
  position: absolute;
  top: 24px;
  left: 48px;
  right: 48px;
  height: 4px;
  background: rgba(226, 232, 240, 0.6);
  border-radius: 2px;
  z-index: 1;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  border-radius: 2px;
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.3);
}

/* Formulaire wizard */
.wizard-form {
  min-height: 400px;
}

.step-content {
  animation: fadeInUp 0.4s ease-out;
}

.step-header {
  text-align: center;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(226, 232, 240, 0.5);
}

.step-title {
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 0.75rem 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
}

.step-title i {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.step-description {
  color: #6b7280;
  font-size: 1rem;
  margin: 0;
  line-height: 1.5;
}

/* Cartes de type de mouvement compactes */
.movement-types-compact {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
}

.movement-type-card-compact {
  position: relative;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.movement-type-card-compact input[type="radio"] {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  cursor: pointer;
  z-index: 2;
}

.card-content-compact {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem 1rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.95) 100%);
  border: 2px solid rgba(226, 232, 240, 0.6);
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  min-height: 100px;
}

.card-content-compact::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  transition: left 0.5s ease;
}

.movement-type-card-compact:hover .card-content-compact::before {
  left: 100%;
}

.movement-type-card-compact:hover .card-content-compact {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  border-color: rgba(0, 123, 255, 0.3);
}

.movement-type-card-compact input:checked + .card-content-compact {
  border-color: #007bff;
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.1) 0%, rgba(0, 86, 179, 0.05) 100%);
  color: #1e293b;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 123, 255, 0.2);
}

.movement-type-card-compact input:checked + .card-content-compact::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 50%, #004085 100%);
  border-radius: 12px 12px 0 0;
}

.card-content-compact i {
  font-size: 1.5rem;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #6b7280 0%, #9ca3af 100%);
  color: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(107, 114, 128, 0.2);
}

.movement-type-card-compact input:checked + .card-content-compact i {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 50%, #004085 100%);
  box-shadow: 0 4px 15px rgba(0, 123, 255, 0.3);
  animation: pulse 2s infinite;
}

.card-content-compact span {
  font-weight: 600;
  font-size: 0.85rem;
  color: #374151;
  text-align: center;
  line-height: 1.3;
}

.movement-type-card-compact input:checked + .card-content-compact span {
  color: #1e293b;
  font-weight: 700;
}

.form-sections {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  animation: fadeInUp 0.6s ease-out;
}

.form-section {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.95) 100%);
  border: 1px solid rgba(226, 232, 240, 0.6);
  border-radius: 20px;
  padding: 2rem;
  position: relative;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.form-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 50%, #004085 100%);
  border-radius: 20px 20px 0 0;
  opacity: 0.8;
}

.form-section:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
  border-color: rgba(0, 123, 255, 0.3);
}

.section-title {
  margin: 0 0 1.5rem 0;
  font-size: 1.3rem;
  font-weight: 700;
  background: linear-gradient(135deg, #1e293b 0%, #475569 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid rgba(226, 232, 240, 0.5);
  position: relative;
  letter-spacing: -0.01em;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 60px;
  height: 2px;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  border-radius: 1px;
}

.movement-types {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
}

.movement-type-card {
  position: relative;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.movement-type-card input[type="radio"] {
  position: absolute;
  opacity: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  cursor: pointer;
  z-index: 2;
}

.card-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.95) 100%);
  border: 2px solid rgba(226, 232, 240, 0.6);
  border-radius: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.card-content::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  transition: left 0.5s ease;
}

.movement-type-card:hover .card-content::before {
  left: 100%;
}

.movement-type-card:hover .card-content {
  transform: translateY(-4px) scale(1.02);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border-color: rgba(0, 123, 255, 0.3);
}

.movement-type-card input:checked + .card-content {
  border-color: #007bff;
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.1) 0%, rgba(0, 86, 179, 0.05) 100%);
  color: #1e293b;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 123, 255, 0.2);
}

.movement-type-card input:checked + .card-content::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 50%, #004085 100%);
  border-radius: 16px 16px 0 0;
}

.card-content i {
  font-size: 2rem;
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #6b7280 0%, #9ca3af 100%);
  color: white;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(107, 114, 128, 0.2);
}

.movement-type-card input:checked + .card-content i {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 50%, #004085 100%);
  box-shadow: 0 8px 25px rgba(0, 123, 255, 0.3);
  animation: pulse 2s infinite;
}

.card-content span {
  font-weight: 600;
  font-size: 0.95rem;
  color: #374151;
  text-align: center;
  line-height: 1.4;
}

.movement-type-card input:checked + .card-content span {
  color: #1e293b;
  font-weight: 700;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  position: relative;
}

.form-group label {
  font-weight: 700;
  color: #374151;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: relative;
  padding-left: 0.5rem;
}

.form-group label::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 3px;
  height: 100%;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  border-radius: 2px;
}

.form-group label.required::after {
  content: ' *';
  color: #ef4444;
  font-weight: 800;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.95) 100%);
  border: 2px solid rgba(226, 232, 240, 0.6);
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 500;
  color: #1e293b;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  position: relative;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #007bff;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.98) 100%);
  box-shadow: 0 0 0 4px rgba(0, 123, 255, 0.1), 0 4px 15px rgba(0, 123, 255, 0.15);
  transform: translateY(-2px);
}

.form-group input.error,
.form-group select.error,
.form-group textarea.error {
  border-color: #ef4444;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(254, 242, 242, 0.95) 100%);
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1), 0 4px 15px rgba(239, 68, 68, 0.15);
}

.form-group textarea {
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  line-height: 1.5;
}

.form-group select {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e");
  background-position: right 1rem center;
  background-repeat: no-repeat;
  background-size: 1.5em 1.5em;
  padding-right: 3rem;
}

.error-message {
  color: #ef4444;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 0.5rem 0.75rem;
  background: linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%);
  border-radius: 8px;
  border-left: 3px solid #ef4444;
  backdrop-filter: blur(10px);
}

.help-text {
  color: #6b7280;
  font-size: 0.8rem;
  font-weight: 500;
  padding: 0.5rem 0.75rem;
  background: linear-gradient(135deg, rgba(107, 114, 128, 0.1) 0%, rgba(156, 163, 175, 0.05) 100%);
  border-radius: 8px;
  border-left: 3px solid #6b7280;
  backdrop-filter: blur(10px);
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 2rem 2.5rem;
  border-top: 1px solid rgba(226, 232, 240, 0.5);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.95) 100%);
  backdrop-filter: blur(15px);
  position: relative;
}

.footer-left {
  display: flex;
}

.footer-right {
  display: flex;
  gap: 1rem;
  align-items: center;
}

.modal-footer::before {
  content: '';
  position: absolute;
  top: -1px;
  left: 50%;
  transform: translateX(-50%);
  width: 100px;
  height: 2px;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  border-radius: 1px;
}

.btn {
  padding: 1rem 2rem;
  border-radius: 16px;
  cursor: pointer;
  font-weight: 700;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  min-width: 160px;
  justify-content: center;
}

.btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  transition: left 0.5s ease;
}

.btn:hover::before {
  left: 100%;
}

.btn-primary {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 50%, #004085 100%);
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #0056b3 0%, #004085 50%, #003366 100%);
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 8px 25px rgba(0, 123, 255, 0.3);
}

.btn-secondary {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 50%, #b91c1c 100%) !important;
  color: white !important;
  border: 2px solid rgba(255, 255, 255, 0.8) !important;
  box-shadow: 0 4px 15px rgba(239, 68, 68, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2) !important;
}

.btn-secondary:hover:not(:disabled) {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 50%, #991b1b 100%) !important;
  color: white !important;
  border: 2px solid rgba(255, 255, 255, 0.9) !important;
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 8px 25px rgba(239, 68, 68, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3) !important;
}

.btn-outline {
  background: transparent !important;
  color: #6b7280 !important;
  border: 2px solid rgba(107, 114, 128, 0.3) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04) !important;
}

.btn-outline:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(107, 114, 128, 0.1) 0%, rgba(156, 163, 175, 0.05) 100%) !important;
  color: #374151 !important;
  border: 2px solid rgba(107, 114, 128, 0.5) !important;
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 6px 20px rgba(107, 114, 128, 0.2) !important;
}

.btn:active {
  transform: translateY(-1px) scale(1.01);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn:disabled:hover {
  transform: none;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.btn i {
  font-size: 1rem;
}

@media (max-width: 768px) {
  .modal-overlay {
    padding: 0.5rem;
  }
  
  .modal-content {
    max-width: 100%;
    border-radius: 20px;
  }
  
  .modal-header {
    padding: 1.5rem 1.5rem 1rem;
  }
  
  .modal-title {
    font-size: 1.5rem;
  }
  
  .modal-title i {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }
  
  .close-btn {
    width: 40px;
    height: 40px;
  }
  
  .modal-body {
    padding: 1.5rem;
  }
  
  .form-section {
    padding: 1.5rem;
  }
  
  .section-title {
    font-size: 1.1rem;
  }
  
  .movement-types {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .movement-type-card .card-content {
    padding: 1.25rem;
  }
  
  .card-content i {
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .form-group input,
  .form-group select,
  .form-group textarea {
    padding: 0.875rem 1rem;
    font-size: 0.9rem;
  }
  
  .modal-footer {
    padding: 1.5rem;
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .btn {
    padding: 0.875rem 1.5rem;
    font-size: 0.875rem;
    min-width: auto;
    width: 100%;
  }
}

@media (max-width: 480px) {
  .modal-overlay {
    padding: 0.25rem;
  }
  
  .modal-header {
    padding: 1rem 1rem 0.75rem;
  }
  
  .modal-title {
    font-size: 1.25rem;
    gap: 0.75rem;
  }
  
  .modal-body {
    padding: 1rem;
  }
  
  .form-section {
    padding: 1rem;
  }
  
  .form-sections {
    gap: 1.5rem;
  }
}
</style>
